/**
 * @module data/iso27001MockData
 * @description Datos mock para el módulo ISO 27001:2022 (SGSI).
 *
 * Contiene la estructura completa de cláusulas (4–10) y sus requisitos según
 * la norma ISO/IEC 27001:2022, con estado de cumplimiento pre-cargado para
 * demostración del sistema sin conexión a Supabase.
 *
 * ## Estructura de datos
 * `ISO27001Clause` (7 cláusulas: 4–10) → `ISO27001Requirement` (N requisitos por cláusula)
 * → `ISO27001Task[]` + `ISO27001Evidence[]`
 *
 * @see ISO27001Context para el contexto React que consume estos datos
 * @see types/iso.ts para los tipos de referentes compartidos con ISO 27002
 */

import type { TechnicalReferent } from '../types/iso';
import { mockTechnicalReferents } from './mockData';

// =====================================================
// TIPOS ESPECÍFICOS ISO 27001
// =====================================================

export interface ISO27001Clause {
  id: string;
  code: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  sortOrder: number;
  requirements: ISO27001Requirement[];
}

export interface ISO27001Requirement {
  id: string;
  code: string;
  title: string;
  description: string;
  guidance?: string;
  clauseId: string;
  parentId?: string;
  status: 'not_started' | 'in_progress' | 'review' | 'completed' | 'non_compliant';
  priority: 'low' | 'medium' | 'high' | 'critical';
  implementation: 'mandatory' | 'recommended' | 'optional';
  dueDate?: string;
  completionDate?: string;
  compliancePercentage: number;
  assignedReferents: TechnicalReferent[];
  evidence: ISO27001Evidence[];
  tasks: ISO27001Task[];
  children: ISO27001Requirement[];
}

export interface ISO27001Task {
  id: string;
  requirementId: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignedTo?: string;
  dueDate?: string;
  completedAt?: string;
  checklist: { id: string; text: string; completed: boolean }[];
}

export interface ISO27001Evidence {
  id: string;
  requirementId: string;
  fileName: string;
  fileType?: string;
  fileSize?: number;
  description?: string;
  uploadedBy?: string;
  isVerified: boolean;
  createdAt: string;
}

export interface ISO27001AuditEntry {
  id: string;
  entityType: 'requirement' | 'task' | 'evidence' | 'clause';
  entityId: string;
  actionType: string;
  actionDescription: string;
  userId?: string;
  userName?: string;
  previousValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  createdAt: string;
}

// =====================================================
// HELPER PARA OBTENER REFERENTES
// =====================================================

const getReferentsByName = (names: string[]): TechnicalReferent[] => {
  return mockTechnicalReferents.filter(ref => names.includes(ref.name));
};

// =====================================================
// CLÁUSULAS ISO 27001:2022
// =====================================================

export const mockISO27001Clauses: ISO27001Clause[] = [
  {
    id: 'clause-4',
    code: '4',
    title: 'Contexto de la Organización',
    description: 'Comprensión de la organización, su contexto, las necesidades y expectativas de las partes interesadas, y determinación del alcance del SGSI.',
    icon: 'Building2',
    color: 'blue',
    sortOrder: 1,
    requirements: []
  },
  {
    id: 'clause-5',
    code: '5',
    title: 'Liderazgo',
    description: 'Compromiso de la alta dirección, establecimiento de la política de seguridad y asignación de roles y responsabilidades.',
    icon: 'Users',
    color: 'purple',
    sortOrder: 2,
    requirements: []
  },
  {
    id: 'clause-6',
    code: '6',
    title: 'Planificación',
    description: 'Acciones para abordar riesgos y oportunidades, evaluación y tratamiento de riesgos, y objetivos de seguridad.',
    icon: 'Target',
    color: 'orange',
    sortOrder: 3,
    requirements: []
  },
  {
    id: 'clause-7',
    code: '7',
    title: 'Apoyo',
    description: 'Recursos, competencia, concienciación, comunicación e información documentada necesarios para el SGSI.',
    icon: 'LifeBuoy',
    color: 'green',
    sortOrder: 4,
    requirements: []
  },
  {
    id: 'clause-8',
    code: '8',
    title: 'Operación',
    description: 'Planificación y control operacional, evaluación y tratamiento de riesgos de seguridad de la información.',
    icon: 'Settings',
    color: 'indigo',
    sortOrder: 5,
    requirements: []
  },
  {
    id: 'clause-9',
    code: '9',
    title: 'Evaluación del Desempeño',
    description: 'Monitoreo, medición, análisis, evaluación, auditoría interna y revisión por la dirección.',
    icon: 'BarChart3',
    color: 'cyan',
    sortOrder: 6,
    requirements: []
  },
  {
    id: 'clause-10',
    code: '10',
    title: 'Mejora',
    description: 'Mejora continua, no conformidades y acciones correctivas del SGSI.',
    icon: 'TrendingUp',
    color: 'emerald',
    sortOrder: 7,
    requirements: []
  }
];

// =====================================================
// REQUISITOS ISO 27001:2022
// =====================================================

export const mockISO27001Requirements: ISO27001Requirement[] = [
  // ─────────────────────────────────────────────────────
  // CLÁUSULA 4: CONTEXTO DE LA ORGANIZACIÓN
  // ─────────────────────────────────────────────────────
  {
    id: 'req-4-1',
    code: '4.1',
    title: 'Comprensión de la organización y de su contexto',
    description: 'La organización debe determinar las cuestiones externas e internas que son pertinentes para su propósito y que afectan su capacidad para lograr los resultados previstos de su SGSI.',
    guidance: 'Identificar factores internos (cultura, estructura, recursos) y externos (legales, tecnológicos, competitivos) que impactan la seguridad de la información.',
    clauseId: 'clause-4',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-03-31',
    compliancePercentage: 60,
    assignedReferents: getReferentsByName(['Dirección']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-4-2',
    code: '4.2',
    title: 'Comprensión de las necesidades y expectativas de las partes interesadas',
    description: 'La organización debe determinar las partes interesadas que son pertinentes al SGSI y los requisitos de estas partes interesadas pertinentes a la seguridad de la información.',
    guidance: 'Identificar stakeholders (empleados, clientes, reguladores, proveedores) y sus requisitos de seguridad.',
    clauseId: 'clause-4',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-03-31',
    compliancePercentage: 50,
    assignedReferents: getReferentsByName(['Dirección', 'Asesoría Jurídica']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-4-3',
    code: '4.3',
    title: 'Determinación del alcance del SGSI',
    description: 'La organización debe determinar los límites y la aplicabilidad del SGSI para establecer su alcance.',
    guidance: 'Definir qué procesos, ubicaciones, tecnologías y activos de información están incluidos en el SGSI.',
    clauseId: 'clause-4',
    status: 'completed',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-02-28',
    completionDate: '2025-02-15',
    compliancePercentage: 100,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Dirección']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-4-4',
    code: '4.4',
    title: 'Sistema de gestión de la seguridad de la información',
    description: 'La organización debe establecer, implementar, mantener y mejorar continuamente un SGSI, de acuerdo con los requisitos de esta norma.',
    guidance: 'Documentar y mantener el SGSI incluyendo procesos, procedimientos e interacciones.',
    clauseId: 'clause-4',
    status: 'in_progress',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-06-30',
    compliancePercentage: 40,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },

  // ─────────────────────────────────────────────────────
  // CLÁUSULA 5: LIDERAZGO
  // ─────────────────────────────────────────────────────
  {
    id: 'req-5-1',
    code: '5.1',
    title: 'Liderazgo y compromiso',
    description: 'La alta dirección debe demostrar liderazgo y compromiso con respecto al SGSI.',
    guidance: 'La dirección debe asegurar recursos, comunicar la importancia del SGSI, dirigir y apoyar al personal.',
    clauseId: 'clause-5',
    status: 'completed',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-01-31',
    completionDate: '2025-01-20',
    compliancePercentage: 100,
    assignedReferents: getReferentsByName(['Dirección']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-5-2',
    code: '5.2',
    title: 'Política',
    description: 'La alta dirección debe establecer una política de seguridad de la información que sea apropiada al propósito de la organización.',
    guidance: 'La política debe incluir objetivos, compromiso de cumplimiento y mejora continua. Debe estar documentada y comunicada.',
    clauseId: 'clause-5',
    status: 'completed',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-02-15',
    completionDate: '2025-02-10',
    compliancePercentage: 100,
    assignedReferents: getReferentsByName(['Dirección', 'Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-5-3',
    code: '5.3',
    title: 'Roles, responsabilidades y autoridades en la organización',
    description: 'La alta dirección debe asegurarse de que las responsabilidades y autoridades para los roles pertinentes a la seguridad de la información se asignen y comuniquen.',
    guidance: 'Definir roles como CISO, propietarios de activos, administradores de sistemas con sus responsabilidades claras.',
    clauseId: 'clause-5',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-03-15',
    compliancePercentage: 75,
    assignedReferents: getReferentsByName(['Dirección', 'Subdirección de gestión de las personas']),
    evidence: [],
    tasks: [],
    children: []
  },

  // ─────────────────────────────────────────────────────
  // CLÁUSULA 6: PLANIFICACIÓN
  // ─────────────────────────────────────────────────────
  {
    id: 'req-6-1',
    code: '6.1',
    title: 'Acciones para abordar riesgos y oportunidades',
    description: 'Al planificar el SGSI, la organización debe considerar las cuestiones del contexto y determinar los riesgos y oportunidades.',
    clauseId: 'clause-6',
    status: 'in_progress',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-04-30',
    compliancePercentage: 55,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-6-1-1',
    code: '6.1.1',
    title: 'Generalidades',
    description: 'La organización debe planificar acciones para abordar riesgos y oportunidades, cómo integrar e implementar estas acciones y evaluar su eficacia.',
    clauseId: 'clause-6',
    parentId: 'req-6-1',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-04-15',
    compliancePercentage: 60,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-6-1-2',
    code: '6.1.2',
    title: 'Evaluación de riesgos de seguridad de la información',
    description: 'La organización debe definir y aplicar un proceso de evaluación de riesgos de seguridad de la información.',
    guidance: 'Establecer criterios de aceptación de riesgos, identificar riesgos, analizar y evaluar riesgos.',
    clauseId: 'clause-6',
    parentId: 'req-6-1',
    status: 'in_progress',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-04-30',
    compliancePercentage: 45,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-6-1-3',
    code: '6.1.3',
    title: 'Tratamiento de riesgos de seguridad de la información',
    description: 'La organización debe definir y aplicar un proceso de tratamiento de riesgos de seguridad de la información.',
    guidance: 'Seleccionar opciones de tratamiento, determinar controles necesarios, producir Declaración de Aplicabilidad.',
    clauseId: 'clause-6',
    parentId: 'req-6-1',
    status: 'not_started',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-05-15',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-6-2',
    code: '6.2',
    title: 'Objetivos de seguridad de la información y planificación para lograrlos',
    description: 'La organización debe establecer objetivos de seguridad de la información en las funciones y niveles pertinentes.',
    guidance: 'Los objetivos deben ser coherentes con la política, medibles, comunicados y actualizados según corresponda.',
    clauseId: 'clause-6',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-04-30',
    compliancePercentage: 50,
    assignedReferents: getReferentsByName(['Dirección', 'Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-6-3',
    code: '6.3',
    title: 'Planificación de los cambios',
    description: 'Cuando la organización determine la necesidad de cambios en el SGSI, estos cambios se deben llevar a cabo de manera planificada.',
    clauseId: 'clause-6',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-05-30',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    evidence: [],
    tasks: [],
    children: []
  },

  // ─────────────────────────────────────────────────────
  // CLÁUSULA 7: APOYO
  // ─────────────────────────────────────────────────────
  {
    id: 'req-7-1',
    code: '7.1',
    title: 'Recursos',
    description: 'La organización debe determinar y proporcionar los recursos necesarios para el establecimiento, implementación, mantenimiento y mejora continua del SGSI.',
    clauseId: 'clause-7',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-04-30',
    compliancePercentage: 70,
    assignedReferents: getReferentsByName(['Dirección', 'Subdirección de recursos físicos']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-2',
    code: '7.2',
    title: 'Competencia',
    description: 'La organización debe determinar la competencia necesaria de las personas que realizan trabajos que afectan el desempeño de la seguridad de la información.',
    guidance: 'Asegurar que el personal tiene la educación, formación o experiencia apropiadas.',
    clauseId: 'clause-7',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-05-15',
    compliancePercentage: 55,
    assignedReferents: getReferentsByName(['Subdirección de gestión de las personas']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-3',
    code: '7.3',
    title: 'Concienciación',
    description: 'Las personas que realizan trabajos bajo el control de la organización deben ser conscientes de la política de seguridad, su contribución a la eficacia del SGSI y las implicaciones de no cumplir.',
    clauseId: 'clause-7',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-05-30',
    compliancePercentage: 40,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Subdirección de gestión de las personas']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-4',
    code: '7.4',
    title: 'Comunicación',
    description: 'La organización debe determinar la necesidad de comunicaciones internas y externas pertinentes al SGSI.',
    guidance: 'Definir qué comunicar, cuándo, a quién, quién debe comunicar y los procesos.',
    clauseId: 'clause-7',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-06-15',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Juan Pablo Galaz']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-5',
    code: '7.5',
    title: 'Información documentada',
    description: 'El SGSI de la organización debe incluir la información documentada requerida por esta norma y la determinada como necesaria para la eficacia del SGSI.',
    clauseId: 'clause-7',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-06-30',
    compliancePercentage: 35,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-5-1',
    code: '7.5.1',
    title: 'Generalidades',
    description: 'El SGSI debe incluir información documentada requerida por la norma y la necesaria para la eficacia del SGSI.',
    clauseId: 'clause-7',
    parentId: 'req-7-5',
    status: 'in_progress',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-06-15',
    compliancePercentage: 40,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-5-2',
    code: '7.5.2',
    title: 'Creación y actualización',
    description: 'Al crear y actualizar la información documentada, la organización debe asegurarse de la identificación, formato y revisión apropiados.',
    clauseId: 'clause-7',
    parentId: 'req-7-5',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-06-20',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-7-5-3',
    code: '7.5.3',
    title: 'Control de la información documentada',
    description: 'La información documentada requerida por el SGSI debe controlarse para asegurar que esté disponible, sea idónea y esté protegida adecuadamente.',
    clauseId: 'clause-7',
    parentId: 'req-7-5',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-06-25',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    evidence: [],
    tasks: [],
    children: []
  },

  // ─────────────────────────────────────────────────────
  // CLÁUSULA 8: OPERACIÓN
  // ─────────────────────────────────────────────────────
  {
    id: 'req-8-1',
    code: '8.1',
    title: 'Planificación y control operacional',
    description: 'La organización debe planificar, implementar y controlar los procesos necesarios para cumplir los requisitos de seguridad de la información.',
    guidance: 'Establecer criterios para los procesos, implementar control de procesos, mantener información documentada.',
    clauseId: 'clause-8',
    status: 'in_progress',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-06-30',
    compliancePercentage: 30,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-8-2',
    code: '8.2',
    title: 'Evaluación de riesgos de seguridad de la información',
    description: 'La organización debe realizar evaluaciones de riesgos de seguridad de la información a intervalos planificados o cuando se propongan u ocurran cambios significativos.',
    clauseId: 'clause-8',
    status: 'not_started',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-07-15',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-8-3',
    code: '8.3',
    title: 'Tratamiento de riesgos de seguridad de la información',
    description: 'La organización debe implementar el plan de tratamiento de riesgos de seguridad de la información.',
    guidance: 'Conservar información documentada de los resultados del tratamiento de riesgos.',
    clauseId: 'clause-8',
    status: 'not_started',
    priority: 'critical',
    implementation: 'mandatory',
    dueDate: '2025-07-30',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Informática']),
    evidence: [],
    tasks: [],
    children: []
  },

  // ─────────────────────────────────────────────────────
  // CLÁUSULA 9: EVALUACIÓN DEL DESEMPEÑO
  // ─────────────────────────────────────────────────────
  {
    id: 'req-9-1',
    code: '9.1',
    title: 'Monitoreo, medición, análisis y evaluación',
    description: 'La organización debe determinar qué necesita monitoreo y medición, los métodos, cuándo se debe realizar y quién debe analizar los resultados.',
    clauseId: 'clause-9',
    status: 'not_started',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-08-15',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-9-2',
    code: '9.2',
    title: 'Auditoría interna',
    description: 'La organización debe llevar a cabo auditorías internas a intervalos planificados para proporcionar información sobre si el SGSI es conforme y está implementado y mantenido eficazmente.',
    clauseId: 'clause-9',
    status: 'not_started',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-09-30',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-9-2-1',
    code: '9.2.1',
    title: 'Generalidades',
    description: 'Planificar, establecer, implementar y mantener programas de auditoría incluyendo frecuencia, métodos, responsabilidades y requisitos de planificación.',
    clauseId: 'clause-9',
    parentId: 'req-9-2',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-09-15',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-9-2-2',
    code: '9.2.2',
    title: 'Programa de auditoría interna',
    description: 'Definir criterios y alcance de cada auditoría, seleccionar auditores objetivos e imparciales, asegurar que los resultados se informan a la dirección.',
    clauseId: 'clause-9',
    parentId: 'req-9-2',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-09-20',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-9-3',
    code: '9.3',
    title: 'Revisión por la dirección',
    description: 'La alta dirección debe revisar el SGSI de la organización a intervalos planificados para asegurar su conveniencia, adecuación y eficacia continuas.',
    clauseId: 'clause-9',
    status: 'not_started',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-10-15',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Dirección']),
    evidence: [],
    tasks: [],
    children: []
  },

  // ─────────────────────────────────────────────────────
  // CLÁUSULA 10: MEJORA
  // ─────────────────────────────────────────────────────
  {
    id: 'req-10-1',
    code: '10.1',
    title: 'Mejora continua',
    description: 'La organización debe mejorar continuamente la conveniencia, adecuación y eficacia del SGSI.',
    clauseId: 'clause-10',
    status: 'not_started',
    priority: 'medium',
    implementation: 'mandatory',
    dueDate: '2025-11-30',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC', 'Dirección']),
    evidence: [],
    tasks: [],
    children: []
  },
  {
    id: 'req-10-2',
    code: '10.2',
    title: 'No conformidad y acción correctiva',
    description: 'Cuando ocurra una no conformidad, la organización debe reaccionar, evaluar la necesidad de acciones correctivas, implementarlas y revisar su eficacia.',
    guidance: 'Tomar acciones para controlar y corregir, hacer frente a las consecuencias, evaluar causas raíz, implementar acciones correctivas.',
    clauseId: 'clause-10',
    status: 'not_started',
    priority: 'high',
    implementation: 'mandatory',
    dueDate: '2025-11-30',
    compliancePercentage: 0,
    assignedReferents: getReferentsByName(['Equipo SIC']),
    evidence: [],
    tasks: [],
    children: []
  }
];

// =====================================================
// CONSTRUIR ESTRUCTURA JERÁRQUICA
// =====================================================

/**
 * Vincula los requisitos a sus cláusulas y construye jerarquía padre-hijo
 */
export const getISO27001ClausesWithRequirements = (): ISO27001Clause[] => {
  return mockISO27001Clauses.map(clause => {
    // Obtener requisitos de nivel superior (sin parentId)
    const topLevelRequirements = mockISO27001Requirements.filter(
      req => req.clauseId === clause.id && !req.parentId
    );

    // Para cada requisito de nivel superior, agregar sus hijos
    const requirementsWithChildren = topLevelRequirements.map(req => {
      const children = mockISO27001Requirements.filter(
        child => child.parentId === req.id
      );
      return { ...req, children };
    });

    return {
      ...clause,
      requirements: requirementsWithChildren
    };
  });
};

// =====================================================
// MÉTRICAS Y ESTADÍSTICAS
// =====================================================

export interface ISO27001Metrics {
  totalRequirements: number;
  completedRequirements: number;
  inProgressRequirements: number;
  notStartedRequirements: number;
  overallCompliance: number;
  byClause: {
    clauseId: string;
    clauseCode: string;
    clauseTitle: string;
    total: number;
    completed: number;
    compliance: number;
  }[];
  criticalPending: number;
  upcomingDeadlines: ISO27001Requirement[];
}

export const calculateISO27001Metrics = (): ISO27001Metrics => {
  const allRequirements = mockISO27001Requirements;

  const completedRequirements = allRequirements.filter(r => r.status === 'completed').length;
  const inProgressRequirements = allRequirements.filter(r => r.status === 'in_progress').length;
  const notStartedRequirements = allRequirements.filter(r => r.status === 'not_started').length;

  const overallCompliance = allRequirements.length > 0
    ? Math.round(allRequirements.reduce((sum, r) => sum + r.compliancePercentage, 0) / allRequirements.length)
    : 0;

  const byClause = mockISO27001Clauses.map(clause => {
    const clauseReqs = allRequirements.filter(r => r.clauseId === clause.id);
    const completed = clauseReqs.filter(r => r.status === 'completed').length;
    const compliance = clauseReqs.length > 0
      ? Math.round(clauseReqs.reduce((sum, r) => sum + r.compliancePercentage, 0) / clauseReqs.length)
      : 0;

    return {
      clauseId: clause.id,
      clauseCode: clause.code,
      clauseTitle: clause.title,
      total: clauseReqs.length,
      completed,
      compliance
    };
  });

  const criticalPending = allRequirements.filter(
    r => r.priority === 'critical' && r.status !== 'completed'
  ).length;

  // Próximos vencimientos (próximos 30 días)
  const today = new Date();
  const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const upcomingDeadlines = allRequirements
    .filter(r => {
      if (!r.dueDate || r.status === 'completed') return false;
      const dueDate = new Date(r.dueDate);
      return dueDate >= today && dueDate <= thirtyDaysFromNow;
    })
    .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
    .slice(0, 5);

  return {
    totalRequirements: allRequirements.length,
    completedRequirements,
    inProgressRequirements,
    notStartedRequirements,
    overallCompliance,
    byClause,
    criticalPending,
    upcomingDeadlines
  };
};
